import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { createManualArticle } from '../../Requests/ArticleBibliographyRequests'
import { getTeamUuid } from '../../utils/cookies'
import SuccessPopup from '../PopupNotifications/SuccessPopup'
import ErrorPopup from '../PopupNotifications/ErrorPopup'

export default function ManualArticleModal({ open, setOpen, projectUuid, onArticleCreated }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    
    const [formData, setFormData] = useState({
        title: '',
        abstract: '',
        authors: [{ name: '' }],
        journal_name: '',
        publication_year: '',
        doi: '',
        landing_page_url: '',
        keywords: []
    })

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleAuthorChange = (index, value) => {
        const newAuthors = [...formData.authors]
        newAuthors[index] = { name: value }
        setFormData(prev => ({
            ...prev,
            authors: newAuthors
        }))
    }

    const addAuthor = () => {
        setFormData(prev => ({
            ...prev,
            authors: [...prev.authors, { name: '' }]
        }))
    }

    const removeAuthor = (index) => {
        if (formData.authors.length > 1) {
            const newAuthors = formData.authors.filter((_, i) => i !== index)
            setFormData(prev => ({
                ...prev,
                authors: newAuthors
            }))
        }
    }

    const handleKeywordsChange = (value) => {
        const keywords = value.split(',').map(keyword => keyword.trim()).filter(keyword => keyword.length > 0)
        setFormData(prev => ({
            ...prev,
            keywords
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!formData.title.trim()) {
            setErrorMessage(t('articleBibliography.manualCreate.errors.titleRequired'))
            return
        }

        try {
            setLoading(true)
            
            // Filter out empty authors
            const validAuthors = formData.authors.filter(author => author.name.trim() !== '')
            
            const articleData = {
                ...formData,
                authors: validAuthors.length > 0 ? validAuthors : [{ name: 'Unknown Author' }],
                publication_year: formData.publication_year ? parseInt(formData.publication_year) : null,
                team_uuid: getTeamUuid(),
                project_id: projectUuid
            }

            await createManualArticle(articleData)
            
            setSuccessMessage(t('articleBibliography.manualCreate.success'))
            setOpen(false)
            
            // Reset form
            setFormData({
                title: '',
                abstract: '',
                authors: [{ name: '' }],
                journal_name: '',
                publication_year: '',
                doi: '',
                landing_page_url: '',
                keywords: []
            })
            
            if (onArticleCreated) {
                onArticleCreated()
            }
        } catch (error) {
            console.error('Error creating manual article:', error)
            setErrorMessage(t('articleBibliography.manualCreate.errors.createFailed'))
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={handleClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                                    <div>
                                        {/* Close Button */}
                                        <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                                            <button
                                                type="button"
                                                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
                                                onClick={handleClose}
                                            >
                                                <span className="sr-only">{t('common.close')}</span>
                                                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                            </button>
                                        </div>

                                        {/* Header */}
                                        <div className="sm:flex sm:items-start">
                                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                                <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                    {t('articleBibliography.manualCreate.title')}
                                                </Dialog.Title>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        {t('articleBibliography.manualCreate.subtitle')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Info Box */}
                                        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-md p-4">
                                            <div className="flex">
                                                <div className="flex-shrink-0">
                                                    <InformationCircleIcon className="h-5 w-5 text-amber-300" aria-hidden="true" />
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm text-amber-700">
                                                        {t('articleBibliography.manualCreate.infoText')}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Form */}
                                        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                                            {/* Title - Required */}
                                            <div>
                                                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
                                                    {t('articleBibliography.manualCreate.fields.title')}
                                                    <span className="text-red-600 ml-0.5">*</span>
                                                </label>
                                                <div className="mt-2">
                                                    <input
                                                        id="title"
                                                        name="title"
                                                        type="text"
                                                        required
                                                        value={formData.title}
                                                        onChange={(e) => handleFormChange('title', e.target.value)}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                        placeholder={t('articleBibliography.manualCreate.placeholders.title')}
                                                    />
                                                </div>
                                            </div>

                                            {/* Authors */}
                                            <div>
                                                <label className="block text-sm font-medium leading-6 text-gray-900">
                                                    {t('articleBibliography.manualCreate.fields.authors')}
                                                </label>
                                                <div className="mt-2 space-y-2">
                                                    {formData.authors.map((author, index) => (
                                                        <div key={index} className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                value={author.name}
                                                                onChange={(e) => handleAuthorChange(index, e.target.value)}
                                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                                placeholder={t('articleBibliography.manualCreate.placeholders.author')}
                                                            />
                                                            {formData.authors.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeAuthor(index)}
                                                                    className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black"
                                                                >
                                                                    {t('common.remove')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={addAuthor}
                                                        className="text-sm text-black hover:text-gray-600"
                                                    >
                                                        + {t('articleBibliography.manualCreate.addAuthor')}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Two Column Layout for remaining fields */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Journal */}
                                                <div>
                                                    <label htmlFor="journal" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articleBibliography.manualCreate.fields.journal')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="journal"
                                                            name="journal"
                                                            type="text"
                                                            value={formData.journal_name}
                                                            onChange={(e) => handleFormChange('journal_name', e.target.value)}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                            placeholder={t('articleBibliography.manualCreate.placeholders.journal')}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Publication Year */}
                                                <div>
                                                    <label htmlFor="year" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articleBibliography.manualCreate.fields.year')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="year"
                                                            name="year"
                                                            type="number"
                                                            min="1900"
                                                            max="2100"
                                                            value={formData.publication_year}
                                                            onChange={(e) => handleFormChange('publication_year', e.target.value)}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                            placeholder={t('articleBibliography.manualCreate.placeholders.year')}
                                                        />
                                                    </div>
                                                </div>

                                                {/* DOI */}
                                                <div>
                                                    <label htmlFor="doi" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articleBibliography.manualCreate.fields.doi')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="doi"
                                                            name="doi"
                                                            type="text"
                                                            value={formData.doi}
                                                            onChange={(e) => handleFormChange('doi', e.target.value)}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                            placeholder={t('articleBibliography.manualCreate.placeholders.doi')}
                                                        />
                                                    </div>
                                                </div>

                                                {/* URL */}
                                                <div>
                                                    <label htmlFor="url" className="block text-sm font-medium leading-6 text-gray-900">
                                                        {t('articleBibliography.manualCreate.fields.url')}
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="url"
                                                            name="url"
                                                            type="url"
                                                            value={formData.landing_page_url}
                                                            onChange={(e) => handleFormChange('landing_page_url', e.target.value)}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                            placeholder={t('articleBibliography.manualCreate.placeholders.url')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Keywords */}
                                            <div>
                                                <label htmlFor="keywords" className="block text-sm font-medium leading-6 text-gray-900">
                                                    {t('articleBibliography.manualCreate.fields.keywords')}
                                                </label>
                                                <div className="mt-2">
                                                    <input
                                                        id="keywords"
                                                        name="keywords"
                                                        type="text"
                                                        value={formData.keywords.join(', ')}
                                                        onChange={(e) => handleKeywordsChange(e.target.value)}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                        placeholder={t('articleBibliography.manualCreate.placeholders.keywords')}
                                                    />
                                                </div>
                                                <p className="mt-1 text-sm text-gray-500">
                                                    {t('articleBibliography.manualCreate.keywordsHint')}
                                                </p>
                                            </div>

                                            {/* Abstract */}
                                            <div>
                                                <label htmlFor="abstract" className="block text-sm font-medium leading-6 text-gray-900">
                                                    {t('articleBibliography.manualCreate.fields.abstract')}
                                                </label>
                                                <div className="mt-2">
                                                    <textarea
                                                        id="abstract"
                                                        name="abstract"
                                                        rows={4}
                                                        value={formData.abstract}
                                                        onChange={(e) => handleFormChange('abstract', e.target.value)}
                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black sm:text-sm sm:leading-6"
                                                        placeholder={t('articleBibliography.manualCreate.placeholders.abstract')}
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </div>

                                    {/* Footer Buttons */}
                                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            onClick={handleSubmit}
                                            className="inline-flex w-full justify-center rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800 disabled:opacity-50 sm:ml-3 sm:w-auto"
                                        >
                                            {loading ? t('articleBibliography.manualCreate.creating') : t('articleBibliography.manualCreate.createButton')}
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                            onClick={handleClose}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Success/Error Popups */}
            {successMessage && (
                <SuccessPopup
                    text={successMessage}
                    handleClose={() => setSuccessMessage('')}
                />
            )}

            {errorMessage && (
                <ErrorPopup
                    text={errorMessage}
                    handleClose={() => setErrorMessage('')}
                />
            )}
        </>
    )
}